// Sidebar.jsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import colors from "../Constant/colors";

/* ===========================
   PERMISSION HELPER
=========================== */
const hasPermission = (permissionKey) => {
  const role = localStorage.getItem("usertype");

  // Admin can access everything
  if (role === "admin") return true;

  const permissions = JSON.parse(
    localStorage.getItem("permissions") || "{}"
  );

  return permissions[permissionKey] === true;
};
const hasAnyPermission = (permissionKeys = []) => {
  const role = localStorage.getItem("usertype");

  // Admin sees everything
  if (role === "admin") return true;

  const permissions = JSON.parse(
    localStorage.getItem("permissions") || "{}"
  );

  return permissionKeys.some(key => permissions[key] === true);
};

/* ===========================
   MENU CONFIG
=========================== */
const menuItems = [
  // ✅ DASHBOARD (ALWAYS ON TOP)
  {
    icon: "fas fa-home",
    text: "Dashboard",
      permissions: [
    "student_attendance_chart",
    "fee_deposit_chart",
    "teacher_attendance_chart",
  ],
    to: "/dashboard",
  },
  {
    icon: "fas fa-images",
    text: "Gallery",
    to: "/gallery",
  },
  { icon: "fas fa-users", text: "Students",
    permissions: [
      "enroll_student",
      "update_student",
      "enroll_guardian",
      "issue_gate_pass",
      "manage_student_attendance",
      "classes",
      "homework_assignment",
      "student_certificate",
      "bulk_upload_students",
    ], to: "/student-management" },
  { icon: "fas fa-university", text: "Staff",
    permissions: [
      "staff_enrollment",
      "staff_attendance",
      "add_designation",
      "bulk_upload_teacher",
    ], to: "/teacher-management" },
  { icon: "fas fa-user", text: "Users",
    permissions: [
      "new_role",
      "permission",
      "reset_password",
      "school_details",
    ],
    to: "/user-management" },

  { icon: "fas fa-bell", text: "Notifications", to: "/notification-management", permission: "notification" },
  { icon: "fas fa-file-alt", text: "Examination", to: "/examination", permission: "examination" },
  { icon: "fas fa-chart-line", text: "Reports", to: "/report-management", permission: "report" },
  { icon: "fas fa-money-bill-wave", text: "Fees", to: "/fee-management", permission: "fees" },
  { icon: "fas fa-dollar-sign", text: "Salary", to: "/salary-management", permission: "salary" },
  { icon: "fas fa-bus", text: "Transportation", to: "/transportation-management", permission: "transportation" },
  { icon: "fas fa-graduation-cap", text: "Promote", to: "/promote-student", permission: "promotion" },
  { icon: "fas fa-tachometer-alt", text: "Hire", to: "/hiring", permission: "hiring" },
];

/* ===========================
   DESKTOP SIDEBAR
=========================== */
const DesktopSidebar = () => {
  const location = useLocation();

  return (
    <aside
      className="w-56 min-h-screen sticky top-0 left-0 text-white shadow-2xl z-50"
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}
    >
      <div className="p-6">
        <ul className="space-y-5">
          {menuItems
            .filter(item => {
  // single permission (Notifications, Fees, etc.)
  if (item.permission) {
    return hasPermission(item.permission);
  }

  // multiple permissions (Students, Staff, Users)
  if (item.permissions) {
    return hasAnyPermission(item.permissions);
  }

  // no permission defined → show
  return true;
})

            .map(item => {
              const isActive = location.pathname === item.to;

              return (
                <li key={item.to} className="rounded-md overflow-hidden">
                  <Link
                    to={item.to}
                    className={`flex items-center space-x-3 p-2.5 rounded-md transition-all duration-300
                      ${
                        isActive
                          ? "bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md scale-[1.02]"
                          : "bg-white text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-md"
                      }`}
                  >
                    <i className={`${item.icon} text-lg`} />
                    <span className="font-semibold">{item.text}</span>
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    </aside>
  );
};

/* ===========================
   MOBILE SIDEBAR
=========================== */
const MobileSidebar = ({ isOpen, toggleSidebar }) => {
  const location = useLocation();

  return (
    <aside
      className={`fixed top-0 left-0 h-full w-64 text-white transform transition-transform duration-300 z-50 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
      style={{
        background: colors.newGradientSidebar,
        boxShadow: `4px 0 15px ${colors.cardShadow}`,
      }}
    >
      <div className="p-5">
        <ul className="space-y-4">
          {menuItems
            .filter(item => !item.permission || hasPermission(item.permission))
            .map(item => {
              const isActive = location.pathname === item.to;

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={toggleSidebar}
                    className={`flex items-center space-x-4 px-3 py-2.5 transition-all
                      ${
                        isActive
                          ? "bg-gradient-to-r from-blue-800 to-blue-600 text-white shadow-md"
                          : "hover:bg-blue-700"
                      }`}
                  >
                    <i className={`${item.icon} text-lg`} />
                    <span>{item.text}</span>
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    </aside>
  );
};

/* ===========================
   MAIN SIDEBAR
=========================== */
const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 768);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    if (!isMobile && isOpen) toggleSidebar();
  }, [isMobile, isOpen, toggleSidebar]);

  return isMobile ? (
    <MobileSidebar isOpen={isOpen} toggleSidebar={toggleSidebar} />
  ) : (
    <DesktopSidebar />
  );
};

export default Sidebar;
